import { useState } from 'react';

const ways = [
  {
    id: 'donate',
    icon: 'ri-hand-heart-line',
    title: 'Make a Donation',
    color: '#7B2D8B',
    tagline: 'Your gift transforms lives',
    description:
      'Financial contributions directly fund our medical outreach, medication support, awareness campaigns, and community support groups for persons living with epilepsy.',
    steps: [
      {
        label: 'Bank Transfer',
        detail: 'Account Name: IKIRU – The Epilepsy Foundation\nBank: [Bank Name Placeholder]\nAccount No: XXXX XXXX XXXX\nSort Code: XXX-XXX',
        icon: 'ri-bank-line',
      },
      {
        label: 'Mobile Money',
        detail: 'Send to: +234 809 955 4455\nReference: IKIRU DONATION',
        icon: 'ri-smartphone-line',
      },
      {
        label: 'Online Transfer',
        detail: 'Contact us at info@ikiruepilepsy.org for secure online payment options and receipts.',
        icon: 'ri-secure-payment-line',
      },
    ],
    impact: [
      { amount: '₦5,000', result: 'Covers one patient\'s medication for a month' },
      { amount: '₦20,000', result: 'Funds a community awareness session' },
      { amount: '₦50,000', result: 'Sponsors a full medical outreach day' },
      { amount: '₦200,000', result: 'Equips a community support group for a year' },
    ],
  },
  {
    id: 'volunteer',
    icon: 'ri-user-heart-line',
    title: 'Volunteer With Us',
    color: '#9B3BAB',
    tagline: 'Give your time and skills',
    description:
      'We welcome passionate individuals who want to make a hands-on difference. Whether you\'re a healthcare professional, student, or community leader — there\'s a role for you.',
    roles: [
      { title: 'Medical Volunteer', desc: 'Doctors, nurses, pharmacists joining our outreach clinics', icon: 'ri-stethoscope-line' },
      { title: 'Community Mobilizer', desc: 'Help organize and run local awareness events', icon: 'ri-megaphone-line' },
      { title: 'Administrative Support', desc: 'Office coordination, data entry, communications', icon: 'ri-file-list-3-line' },
      { title: 'Social Media Advocate', desc: 'Create and share content to spread epilepsy awareness', icon: 'ri-share-line' },
      { title: 'Fundraising Champion', desc: 'Organize events and drives in your community', icon: 'ri-trophy-line' },
      { title: 'Counsellor / Listener', desc: 'Provide emotional support to patients and families', icon: 'ri-mental-health-line' },
    ],
  },
  {
    id: 'partner',
    icon: 'ri-links-line',
    title: 'Partner With Us',
    color: '#6B1D7B',
    tagline: 'Amplify impact together',
    description:
      'We collaborate with corporations, NGOs, government agencies, hospitals, and faith-based organizations to scale our reach and deepen our impact across Nigeria.',
    partnerTypes: [
      { type: 'Corporate Sponsors', desc: 'Fund programs in exchange for brand visibility and CSR impact reporting', icon: 'ri-building-2-line' },
      { type: 'Healthcare Partners', desc: 'Hospitals and clinics providing referrals, facilities, and medical expertise', icon: 'ri-hospital-line' },
      { type: 'NGO Collaborators', desc: 'Joint programs, shared resources, and co-advocacy campaigns', icon: 'ri-global-line' },
      { type: 'Government Agencies', desc: 'Policy advocacy, public health integration, and funding support', icon: 'ri-government-line' },
    ],
    benefits: [
      'Co-branded awareness campaigns',
      'CSR impact reports and certificates',
      'Speaking opportunities at IKIRU events',
      'Recognition on our website and materials',
      'Access to our beneficiary network',
    ],
  },
  {
    id: 'fundraise',
    icon: 'ri-trophy-line',
    title: 'Fundraise for IKIRU',
    color: '#8B2D9B',
    tagline: 'Rally your community',
    description:
      'You don\'t have to be part of our team to raise funds for us. Organize a fundraiser in your school, workplace, church, mosque, or social circle and channel the proceeds to IKIRU.',
    ideas: [
      { idea: 'Birthday Fundraiser', desc: 'Ask friends to donate instead of gifts on your special day', icon: 'ri-cake-line' },
      { idea: 'Charity Walk / Run', desc: 'Organize a sponsored walk or run in your neighborhood', icon: 'ri-run-line' },
      { idea: 'Bake Sale / Food Drive', desc: 'Host a community sale and donate proceeds to IKIRU', icon: 'ri-store-line' },
      { idea: 'Online Campaign', desc: 'Launch a social media fundraising challenge with our hashtag', icon: 'ri-hashtag' },
      { idea: 'Corporate Match', desc: 'Ask your employer to match your personal donation', icon: 'ri-building-line' },
      { idea: 'Talent Show / Concert', desc: 'Organize an event and sell tickets for the cause', icon: 'ri-music-line' },
    ],
  },
  {
    id: 'advocate',
    icon: 'ri-megaphone-line',
    title: 'Advocate & Raise Awareness',
    color: '#7B2D8B',
    tagline: 'Your voice matters',
    description:
      'Stigma is one of the biggest barriers for people living with epilepsy. You can help break it by speaking up, sharing facts, and championing inclusion in your community.',
    actions: [
      { action: 'Share on Social Media', desc: 'Follow and share our posts to spread epilepsy awareness', icon: 'ri-share-forward-line' },
      { action: 'Wear Purple', desc: 'Show solidarity on Purple Day (March 26) every year', icon: 'ri-t-shirt-line' },
      { action: 'Educate Your Circle', desc: 'Share first-aid tips and facts about epilepsy with family and friends', icon: 'ri-book-open-line' },
      { action: 'Write to Leaders', desc: 'Advocate for better epilepsy policies and healthcare funding', icon: 'ri-mail-send-line' },
      { action: 'Join Our Campaigns', desc: 'Participate in our annual awareness drives and outreach events', icon: 'ri-calendar-event-line' },
    ],
  },
];

export default function WaysToGive() {
  const [activeTab, setActiveTab] = useState('donate');
  const active = ways.find((w) => w.id === activeTab)!;

  return (
    <section id="donate" className="py-24 bg-[#FAF7FC]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-purple-200 bg-purple-50 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#7B2D8B]"></span>
            <span className="text-[#7B2D8B] text-sm font-medium tracking-wide">Ways to Support</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
            How You Can <span className="text-[#7B2D8B]">Help</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
            Choose the way that works best for you. Every action counts in our mission to transform epilepsy care across Africa.
          </p>
        </div>

        {/* Tab Nav */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {ways.map((w) => (
            <button
              key={w.id}
              onClick={() => setActiveTab(w.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === w.id
                  ? 'bg-[#7B2D8B] text-white'
                  : 'bg-white border border-purple-200 text-gray-600 hover:border-[#7B2D8B] hover:text-[#7B2D8B]'
              }`}
            >
              <i className={`${w.icon} text-base`}></i>
              {w.title}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-3xl border border-purple-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#2D0A35] to-[#7B2D8B] p-10 md:p-14">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/15 shrink-0">
                <i className={`${active.icon} text-white text-3xl`}></i>
              </div>
              <div>
                <div className="text-purple-300 text-sm font-medium tracking-widest uppercase mb-2">{active.tagline}</div>
                <h3 className="text-3xl md:text-4xl font-extrabold text-white mb-3">{active.title}</h3>
                <p className="text-purple-200 text-base leading-relaxed max-w-2xl">{active.description}</p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-10 md:p-14">
            {/* DONATE */}
            {active.id === 'donate' && active.steps && (
              <div className="space-y-10">
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-6">Payment Methods</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {active.steps.map((s) => (
                      <div key={s.label} className="rounded-2xl border border-purple-100 p-6 bg-[#FAF7FC]">
                        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#7B2D8B]/10 mb-4">
                          <i className={`${s.icon} text-[#7B2D8B] text-xl`}></i>
                        </div>
                        <div className="font-bold text-gray-900 mb-2">{s.label}</div>
                        <div className="text-gray-500 text-sm leading-relaxed whitespace-pre-line">{s.detail}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-6">Your Impact</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {active.impact && active.impact.map((imp) => (
                      <div key={imp.amount} className="rounded-2xl bg-gradient-to-br from-[#7B2D8B]/10 to-[#C084FC]/10 border border-purple-100 p-5 text-center">
                        <div className="text-2xl font-extrabold text-[#7B2D8B] mb-2">{imp.amount}</div>
                        <div className="text-gray-600 text-sm leading-snug">{imp.result}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3 p-5 rounded-2xl bg-amber-50 border border-amber-200">
                  <i className="ri-information-line text-amber-600 text-xl shrink-0"></i>
                  <p className="text-amber-700 text-sm leading-relaxed">
                    All donations are acknowledged with a receipt. For large donations or in-kind contributions, please contact us at <strong>info@ikiruepilepsy.org</strong> or call <strong>+234 809 955 4455</strong>.
                  </p>
                </div>
              </div>
            )}

            {/* VOLUNTEER */}
            {active.id === 'volunteer' && active.roles && (
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-6">Volunteer Roles Available</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {active.roles.map((r) => (
                    <div key={r.title} className="rounded-2xl border border-purple-100 p-6 bg-[#FAF7FC] hover:border-[#7B2D8B] transition-colors group">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#7B2D8B]/10 mb-4 group-hover:bg-[#7B2D8B]/20 transition-colors">
                        <i className={`${r.icon} text-[#7B2D8B] text-xl`}></i>
                      </div>
                      <div className="font-bold text-gray-900 mb-2">{r.title}</div>
                      <div className="text-gray-500 text-sm leading-relaxed">{r.desc}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 p-6 rounded-2xl bg-[#7B2D8B]/5 border border-purple-100 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <i className="ri-heart-pulse-line text-[#7B2D8B] text-3xl shrink-0"></i>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Ready to volunteer?</div>
                    <p className="text-gray-500 text-sm">Fill in the form below and select &quot;Volunteer&quot; as your interest. Our team will reach out within 48 hours.</p>
                  </div>
                </div>
              </div>
            )}

            {/* PARTNER */}
            {active.id === 'partner' && active.partnerTypes && (
              <div className="space-y-10">
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-6">Partnership Types</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {active.partnerTypes.map((p) => (
                      <div key={p.type} className="rounded-2xl border border-purple-100 p-6 bg-[#FAF7FC] flex gap-4">
                        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#7B2D8B]/10 shrink-0">
                          <i className={`${p.icon} text-[#7B2D8B] text-xl`}></i>
                        </div>
                        <div>
                          <div className="font-bold text-gray-900 mb-1">{p.type}</div>
                          <div className="text-gray-500 text-sm leading-relaxed">{p.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-5">Partner Benefits</h4>
                  <ul className="space-y-3">
                    {active.benefits && active.benefits.map((b) => (
                      <li key={b} className="flex items-center gap-3 text-gray-700 text-sm">
                        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#7B2D8B]/15 shrink-0">
                          <i className="ri-check-line text-[#7B2D8B] text-xs"></i>
                        </div>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* FUNDRAISE */}
            {active.id === 'fundraise' && active.ideas && (
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-6">Fundraising Ideas</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {active.ideas.map((idea) => (
                    <div key={idea.idea} className="rounded-2xl border border-purple-100 p-6 bg-[#FAF7FC] hover:border-[#7B2D8B] transition-colors group">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#7B2D8B]/10 mb-4 group-hover:bg-[#7B2D8B]/20 transition-colors">
                        <i className={`${idea.icon} text-[#7B2D8B] text-xl`}></i>
                      </div>
                      <div className="font-bold text-gray-900 mb-2">{idea.idea}</div>
                      <div className="text-gray-500 text-sm leading-relaxed">{idea.desc}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 p-6 rounded-2xl bg-[#7B2D8B]/5 border border-purple-100">
                  <div className="font-bold text-gray-900 mb-2">How to get started</div>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Contact us at <strong>info@ikiruepilepsy.org</strong> to register your fundraiser. We&apos;ll provide you with promotional materials, a fundraising guide, and a dedicated point of contact to support your campaign.
                  </p>
                </div>
              </div>
            )}

            {/* ADVOCATE */}
            {active.id === 'advocate' && active.actions && (
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-6">Ways to Advocate</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {active.actions.map((a) => (
                    <div key={a.action} className="rounded-2xl border border-purple-100 p-6 bg-[#FAF7FC] hover:border-[#7B2D8B] transition-colors group">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#7B2D8B]/10 mb-4 group-hover:bg-[#7B2D8B]/20 transition-colors">
                        <i className={`${a.icon} text-[#7B2D8B] text-xl`}></i>
                      </div>
                      <div className="font-bold text-gray-900 mb-2">{a.action}</div>
                      <div className="text-gray-500 text-sm leading-relaxed">{a.desc}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex flex-wrap gap-4">
                  {['#IKIRUEpilepsy', '#PurpleDay', '#EpilepsyAwareness', '#BreakTheStigma', '#GivingLifeHopeDignity'].map((tag) => (
                    <span key={tag} className="px-4 py-2 rounded-full bg-[#7B2D8B]/10 text-[#7B2D8B] text-sm font-semibold cursor-pointer hover:bg-[#7B2D8B]/20 transition-colors">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
